import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button } from "components/atoms";
import {
  DocumentUploadModal,
  RadioButtonGroup,
  GeneralSelectInput,
  MessageTextAreaInput,
  MultiChoiceCheckboxGroup,
  NumberInput,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import {
  GenuineStudentFormValues,
  whyStudyAbroadOptions,
  researchActivitiesOptions,
  postStudyLocationOptions,
  demandLevelOptions,
  incomeChangeOptions,
  qualificationUseOptions,
  FileWithType,
} from "types";

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set(["genuineStudentData"]),
};

interface GSCourseCareerSectionProps {
  currentSubStage?: string;
}

const GSCourseCareerSection: React.FC<GSCourseCareerSectionProps> = ({
  currentSubStage = "employment",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<GenuineStudentFormValues>();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    resume?: { id: string; fileName: string };
    employment1Reference?: { id: string; fileName: string };
    highSchoolTranscript?: { id: string; fileName: string };
    tertiaryTranscript1?: { id: string; fileName: string };
    tertiaryTranscript2?: { id: string; fileName: string };
    tertiaryTranscript3?: { id: string; fileName: string };
    providerCorrespondence?: { id: string; fileName: string };
    employerCorrespondence?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024,
      enableMagicNumberValidation: false,
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const courseCareerDocTypes = [
    "RESUME",
    "EMPLOYMENT_1_REFERENCE",
    "HIGH_SCHOOL_TRANSCRIPT",
    "TERTIARY_TRANSCRIPT_1",
    "TERTIARY_TRANSCRIPT_2",
    "TERTIARY_TRANSCRIPT_3",
    "PROVIDER_CORRESPONDENCE",
    "EMPLOYER_CORRESPONDENCE",
  ];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(courseCareerDocTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs =
            await documentService.fetchDocumentsWithMapping(
              courseCareerDocTypes,
            );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "resume",
        label: "CV/Resume",
        description: "Current CV showing education and work history",
        accept: ".pdf,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.resume,
        stage: "employment",
      },
      {
        id: "employment1reference",
        label: "Employment Reference",
        description: "Professional reference from current employer",
        accept: ".pdf,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.employment1Reference,
        stage: "employment",
      },
      {
        id: "highschooltranscript",
        label: "High School Transcript",
        description: "Official high school academic transcript",
        accept: ".pdf,.jpg,.jpeg",
        required: true,
        uploaded: !!uploadedDocuments.highSchoolTranscript,
        stage: "course",
      },
      {
        id: "tertiarytranscript1",
        label: "Recent Tertiary Transcript",
        description: "Most recent tertiary academic transcript",
        accept: ".pdf,.jpg,.jpeg",
        required: true,
        uploaded: !!uploadedDocuments.tertiaryTranscript1,
        stage: "course",
      },
      {
        id: "tertiarytranscript2",
        label: "Tertiary Transcript 2",
        description: "Additional tertiary academic transcript (if applicable)",
        accept: ".pdf,.jpg,.jpeg",
        required: false,
        uploaded: !!uploadedDocuments.tertiaryTranscript2,
        stage: "course",
      },
      {
        id: "tertiarytranscript3",
        label: "Tertiary Transcript 3",
        description: "Additional tertiary academic transcript (if applicable)",
        accept: ".pdf,.jpg,.jpeg",
        required: false,
        uploaded: !!uploadedDocuments.tertiaryTranscript3,
        stage: "course",
      },
      {
        id: "providercorrespondence",
        label: "Provider Correspondence",
        description: "Email/correspondence with education provider",
        accept: ".pdf,.doc,.docx",
        required: false,
        uploaded: !!uploadedDocuments.providerCorrespondence,
        stage: "course",
      },
      {
        id: "employercorrespondence",
        label: "Employer Correspondence",
        description: "Correspondence with potential employers or job offers",
        accept: ".pdf,.doc,.docx",
        required: values.hasJobOffer === true,
        uploaded: !!uploadedDocuments.employerCorrespondence,
        stage: "career",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const renderEmploymentSection = () => (
    <div className="employment-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Employment Background</h4>
        </div>
        <div className="form-fields">
          <NumberInput
            id="totalWorkExperience"
            label="Total years of work experience:"
            placeholder="Enter number of years"
            getFieldProps={() => ({
              value: values.totalWorkExperience || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.totalWorkExperience}
            touched={touched.totalWorkExperience}
          />

          <RadioButtonGroup
            name="workRelatedToStudy"
            label="Is your work experience related to your chosen field of study?"
            value={values.workRelatedToStudy}
            onChange={(value) => {
              setFieldValue("workRelatedToStudy", value);
              setFieldTouched("workRelatedToStudy", true);
            }}
          />

          <RadioButtonGroup
            name="employerSupportsStudy"
            label="Does your current/previous employer support your study plans?"
            value={values.employerSupportsStudy}
            onChange={(value) => {
              setFieldValue("employerSupportsStudy", value);
              setFieldTouched("employerSupportsStudy", true);
            }}
          />

          <MessageTextAreaInput
            id="employmentHistoryRelevance"
            label="Employment History & Relevance (150 words max)"
            placeholder="Describe your employment history and how it relates to your chosen course. Include specific roles, responsibilities, achievements, and skills gained. Explain how your work experience has prepared you for this study."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.employmentHistoryRelevance || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.employmentHistoryRelevance}
            touched={touched.employmentHistoryRelevance}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Employment Documents"
        description="Please attach your employment and academic documents:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderCourseSection = () => (
    <div className="course-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Course Selection</h4>
        </div>
        <div className="form-fields">
          <div className="form-field">
            <RadioButtonGroup
              name="courseAvailableHome"
              label="Is this course available in your home country?"
              value={values.courseAvailableHome}
              onChange={(value) => {
                setFieldValue("courseAvailableHome", value);
                setFieldTouched("courseAvailableHome", true);
                if (value === false) {
                  setFieldValue("whyStudyAbroadReasons", []);
                }
              }}
            />
            {values.courseAvailableHome === true && (
              <MultiChoiceCheckboxGroup
                name="whyStudyAbroadReasons"
                label="Why study abroad? (Check all that apply)"
                options={whyStudyAbroadOptions}
                value={values.whyStudyAbroadReasons || []}
                onChange={(values: string[]) =>
                  setFieldValue("whyStudyAbroadReasons", values)
                }
                onOtherChange={(value) =>
                  setFieldValue("otherWhyStudyAbroadReasons", value)
                }
                showOther={true}
                otherPlaceholder="Please specify"
                required={true}
              />
            )}
          </div>

          <RadioButtonGroup
            name="courseRelatesPrevEducation"
            label="Does this course directly relate to your previous education?"
            value={values.courseRelatedPrevEducation}
            onChange={(value) => {
              setFieldValue("courseRelatedPrevEducation", value);
              setFieldTouched("courseRelatedPrevEducation", true);
            }}
          />

          <RadioButtonGroup
            name="courseRequiredLicensing"
            label="Is this course required for professional licensing or certification?"
            value={values.courseRequiredLicensing}
            onChange={(value) => {
              setFieldValue("courseRequiredLicensing", value);
              setFieldTouched("courseRequiredLicensing", true);
            }}
          />

          <div className="form-field">
            <RadioButtonGroup
              name="researchedAlternatives"
              label="Have you researched alternative education providers?"
              value={values.researchedAlternatives}
              onChange={(value) => {
                setFieldValue("researchedAlternatives", value);
                setFieldTouched("researchedAlternatives", true);
                if (value === false) {
                  setFieldValue("researchActivities", []);
                }
              }}
            />
            {values.researchedAlternatives === true && (
              <MultiChoiceCheckboxGroup
                name="researchActivities"
                label="Research activities completed:"
                options={researchActivitiesOptions}
                value={values.researchActivities || []}
                onChange={(values: string[]) =>
                  setFieldValue("researchActivities", values)
                }
              />
            )}
          </div>

          <div className="form-field">
            <RadioButtonGroup
              name="consideredOtherCountries"
              label="Did you consider studying in other countries?"
              value={values.consideredOtherCountries}
              onChange={(value) => {
                setFieldValue("consideredOtherCountries", value);
                setFieldTouched("consideredOtherCountries", true);
                if (value === false) {
                  setFieldValue("countriesConsidered", "");
                }
              }}
            />
            {values.consideredOtherCountries === true && (
              <MessageTextAreaInput
                id="countriesConsidered"
                label="Which countries did you consider?"
                placeholder="List the countries"
                rows="2"
                maxLength={200}
                getFieldProps={() => ({
                  value: values.countriesConsidered || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.countriesConsidered}
                touched={touched.countriesConsidered}
              />
            )}
          </div>

          <MessageTextAreaInput
            id="whyThisCourse"
            label="Why This Course (150 words max)"
            placeholder="Explain why you chose this specific course. Detail how it aligns with your previous education and/or work experience. Describe the specific skills and knowledge you will gain and why they are important to your goals."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.whyThisCourse || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.whyThisCourse}
            touched={touched.whyThisCourse}
          />

          <MessageTextAreaInput
            id="whyThisProvider"
            label="Why This Provider (150 words max)"
            placeholder="Explain why you selected this specific education provider. Demonstrate your research by referencing specific program features, faculty expertise, facilities, industry connections, or other factors that make this provider the best choice."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.whyThisProvider || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.whyThisProvider}
            touched={touched.whyThisProvider}
          />

          <MessageTextAreaInput
            id="whyThisCountry"
            label="Why This Country (150 words max)"
            placeholder="Explain why you chose to study in this country rather than your home country or another country. Include specific advantages such as education quality, language of instruction, industry connections, research opportunities, or other relevant factors."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.whyThisCountry || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.whyThisCountry}
            touched={touched.whyThisCountry}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Course Documents"
        description="Please attach your academic and course research documents:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderCareerSection = () => (
    <div className="career-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Career Planning</h4>
        </div>
        <div className="form-fields">
          <GeneralSelectInput
            id="postGraduationWorkLocation"
            label="Where do you plan to work after graduation?"
            options={postStudyLocationOptions}
            value={values.postGraduationWorkLocation || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.postGraduationWorkLocation}
            touched={touched.postGraduationWorkLocation}
          />

          <div className="form-field">
            <RadioButtonGroup
              name="hasSpecificCareerGoal"
              label="Do you have a specific career goal?"
              value={values.hasSpecificCareerGoal}
              onChange={(value) => {
                setFieldValue("hasSpecificCareerGoal", value);
                setFieldTouched("hasSpecificCareerGoal", true);
                if (value === false) {
                  setFieldValue("targetRole", "");
                }
              }}
            />
            {values.hasSpecificCareerGoal === true && (
              <MessageTextAreaInput
                id="targetRole"
                label="Target role:"
                placeholder="Describe your target role"
                rows="2"
                maxLength={200}
                getFieldProps={() => ({
                  value: values.targetRole || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.targetRole}
                touched={touched.targetRole}
              />
            )}
          </div>

          <GeneralSelectInput
            id="qualificationDemand"
            label="Is there demand for this qualification in your intended work location?"
            options={demandLevelOptions}
            value={values.qualificationDemand || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.qualificationDemand}
            touched={touched.qualificationDemand}
          />

          <GeneralSelectInput
            id="expectedIncomeChange"
            label="Expected income change after qualification:"
            options={incomeChangeOptions}
            value={values.expectedIncomeChange || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.expectedIncomeChange}
            touched={touched.expectedIncomeChange}
          />

          <MultiChoiceCheckboxGroup
            name="qualificationUsePlans"
            label="How will you use this qualification? (Check all that apply)"
            options={qualificationUseOptions}
            value={values.qualificationUsePlans || []}
            onChange={(values: string[]) =>
              setFieldValue("qualificationUsePlans", values)
            }
            onOtherChange={(value) =>
              setFieldValue("otherQualificationUsePlans", value)
            }
            showOther={true}
            otherPlaceholder="Please specify"
            required={true}
          />

          <RadioButtonGroup
            name="hasJobOffer"
            label="Do you have a job offer or position waiting?"
            value={values.hasJobOffer}
            onChange={(value) => {
              setFieldValue("hasJobOffer", value);
              setFieldTouched("hasJobOffer", true);
            }}
          />

          <RadioButtonGroup
            name="returnToFamilyBusiness"
            label="Will you return to a family business?"
            value={values.returnToFamilyBusiness}
            onChange={(value) => {
              setFieldValue("returnToFamilyBusiness", value);
              setFieldTouched("returnToFamilyBusiness", true);
            }}
          />

          <MessageTextAreaInput
            id="careerBenefitsPlans"
            label="Career Benefits & Plans (150 words max)"
            placeholder="Explain how completing this course will benefit your career. Detail your specific career plans including target roles, organizations, or sectors. Describe the employment opportunities this qualification will create and the expected salary/career progression in your intended work location."
            rows="6"
            maxLength={150}
            getFieldProps={() => ({
              value: values.careerBenefitsPlans || "",
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.careerBenefitsPlans}
            touched={touched.careerBenefitsPlans}
          />

          {documentService.countRequiredDocs(
            getDocumentFields(currentSubStage),
          ) > 0 && (
            <div className="documents-section">
              <div className="upload-header">
                <Button
                  type="button"
                  text="Upload Documents"
                  onClick={() => setShowDocumentModal(true)}
                  variant="border"
                  width="10rem"
                />
                <div className="docs-counter">
                  <span className="uploaded-count">
                    {documentService.countUploadedDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="separator">/</span>
                  <span className="total-count">
                    {documentService.countRequiredDocs(
                      getDocumentFields(currentSubStage),
                    )}
                  </span>
                  <span className="docs-label">documents</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload Career Documents"
        description="Please attach job market research and employer correspondence:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  switch (currentSubStage) {
    case "employment":
      return renderEmploymentSection();
    case "course":
      return renderCourseSection();
    case "career":
      return renderCareerSection();
    default:
      return renderEmploymentSection();
  }
};

export default GSCourseCareerSection;
